// Paso 5 — TUS DATOS
//
// Form: nombre, email, teléfono (opcional), ciudad, tipo de sala, comentario.
// Checkbox RGPD obligatorio. Al enviar: generatePDF → sendBudget → step 6.

import { state, update } from '/src/shared/state.js';
import { ctaButton, setButtonDisabled } from '/src/ui/components/cta-button.js';

const ROOM_TYPES = [
  { id: 'oficina', label: 'Oficina' },
  { id: 'aula', label: 'Aula' },
  { id: 'restaurante', label: 'Restaurante' },
  { id: 'sala-reuniones', label: 'Sala de reuniones' },
  { id: 'consulta', label: 'Consulta médica' },
  { id: 'otro', label: 'Otro' },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

async function loadPdf() {
  try {
    return await import('/src/pdf/generator.js');
  } catch (_err) {
    return null;
  }
}
async function loadEmail() {
  try {
    return await import('/src/pdf/email.js');
  } catch (_err) {
    return null;
  }
}

const RESERVE_URL =
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_RESERVE_BUDGET_URL) ||
  'https://us-central1-isigenioisinac.cloudfunctions.net/reserveBudgetNumber';

async function reserveBudgetId(meta) {
  try {
    const res = await fetch(RESERVE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(meta || {}),
    });
    const data = await res.json();
    if (data?.ok && data.id) return data.id;
  } catch (err) {
    console.warn('[step5] reserveBudgetId falló — usando fallback:', err);
  }
  return null;
}

async function buildAllOptions(room, surfaces) {
  try {
    const [{ PRODUCTS }, { suggestPanels }, { calculateTR60 }] = await Promise.all([
      import('/src/core/catalog.js'),
      import('/src/core/coverage.js'),
      import('/src/core/acoustics.js'),
    ]);
    const { flattenResults } = await import('/src/shared/results-adapter.js');
    return PRODUCTS.map((p) => {
      let cov, results, totalPanels = 0;
      try {
        cov = suggestPanels({ room, product: p });
        totalPanels = cov?.totalQuantity ?? 0;
      } catch (_) { cov = null; }
      try {
        const raw = calculateTR60({
          room,
          surfaces,
          panels: [{ count: totalPanels, product: p }],
          product: p,
        });
        results = flattenResults(raw);
      } catch (_) { results = null; }
      return {
        product: p,
        totalPanels,
        totalPriceEUR: (p.precio || 0) * totalPanels,
        coverage: cov,
        results,
      };
    });
  } catch (err) {
    console.warn('[step5] buildAllOptions falló:', err);
    return null;
  }
}

export function renderStep5(host, { next, back }) {
  host.innerHTML = `
    <header class="step__header">
      <h1 class="step__title" id="step-5-title">Tus datos</h1>
      <p class="step__subtitle">Te enviamos el PDF al correo y nos quedamos una copia.</p>
    </header>

    <form id="client-form" class="form-grid" novalidate>
      <label class="field">
        <span class="field__label">Nombre completo</span>
        <input class="field__input" name="name" type="text" required autocomplete="name" />
      </label>

      <label class="field">
        <span class="field__label">Email</span>
        <input class="field__input" name="email" type="email" required autocomplete="email" inputmode="email" />
      </label>

      <label class="field">
        <span class="field__label">Teléfono (opcional)</span>
        <input class="field__input" name="phone" type="tel" autocomplete="tel" inputmode="tel" />
      </label>

      <label class="field">
        <span class="field__label">Ciudad</span>
        <input class="field__input" name="city" type="text" autocomplete="address-level2" />
      </label>

      <label class="field field--full">
        <span class="field__label">Tipo de sala</span>
        <select class="field__select" name="roomType" required>
          <option value="">Selecciona…</option>
          ${ROOM_TYPES.map((r) => `<option value="${r.id}">${r.label}</option>`).join('')}
        </select>
      </label>

      <label class="field field--full">
        <span class="field__label">Comentario (opcional)</span>
        <textarea class="field__textarea" name="notes" rows="3" placeholder="Cuéntanos cualquier detalle relevante…"></textarea>
      </label>

      <label class="checkbox field--full">
        <input type="checkbox" name="rgpd" required />
        <span>
          He leído la <a href="/politica-privacidad" target="_blank" rel="noopener">política de privacidad</a>
          y acepto que ISINAC me envíe el presupuesto.
        </span>
      </label>
    </form>

    <div id="form-status" role="status" aria-live="polite" style="text-align:center; color: var(--c-gray); min-height: 1.5em;"></div>

    <div class="sticky-cta" id="sticky-cta"></div>
  `;

  const form = host.querySelector('#client-form');
  const statusEl = host.querySelector('#form-status');

  // Hidratar
  for (const [k, v] of Object.entries(state.client || {})) {
    const input = form.elements[k];
    if (input && typeof v === 'string') input.value = v;
  }

  const backBtn = ctaButton({ label: 'Atrás', variant: 'secondary', onClick: back });
  const submitBtn = ctaButton({
    label: 'Enviar presupuesto',
    variant: 'primary',
    type: 'submit',
    disabled: true,
    onClick: (e) => {
      e.preventDefault();
      submit();
    },
  });
  const sticky = host.querySelector('#sticky-cta');
  sticky.appendChild(backBtn);
  sticky.appendChild(submitBtn);

  // Live update del state + validación.
  form.addEventListener('input', () => {
    const data = readForm();
    update({ client: data });
    setButtonDisabled(submitBtn, !validate(false));
  });
  form.addEventListener('change', () => {
    const data = readForm();
    update({ client: data });
    setButtonDisabled(submitBtn, !validate(false));
  });

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    submit();
  });

  function readForm() {
    const fd = new FormData(form);
    return {
      name: (fd.get('name') || '').toString().trim(),
      email: (fd.get('email') || '').toString().trim(),
      phone: (fd.get('phone') || '').toString().trim(),
      city: (fd.get('city') || '').toString().trim(),
      roomType: (fd.get('roomType') || '').toString(),
      notes: (fd.get('notes') || '').toString().trim(),
      rgpd: !!fd.get('rgpd'),
    };
  }

  function validate(showErrors) {
    const d = readForm();
    let ok = true;
    clearErrors();

    if (!d.name) {
      ok = false;
      if (showErrors) markError('name', 'Indícanos tu nombre');
    }
    if (!EMAIL_RE.test(d.email)) {
      ok = false;
      if (showErrors) markError('email', 'Email no válido');
    }
    if (!d.roomType) {
      ok = false;
      if (showErrors) markError('roomType', 'Selecciona el tipo de sala');
    }
    if (!d.rgpd) {
      ok = false;
      if (showErrors) statusEl.textContent = 'Acepta la política de privacidad para continuar.';
    }
    return ok;
  }

  function clearErrors() {
    form.querySelectorAll('.field--invalid').forEach((f) => f.classList.remove('field--invalid'));
    form.querySelectorAll('.field__error').forEach((e) => e.remove());
    statusEl.textContent = '';
  }
  function markError(name, msg) {
    const input = form.elements[name];
    if (!input) return;
    const wrap = input.closest('.field') || input.closest('.checkbox');
    if (!wrap) return;
    wrap.classList.add('field--invalid');
    const span = document.createElement('span');
    span.className = 'field__error';
    span.textContent = msg;
    wrap.appendChild(span);
  }

  async function submit() {
    if (!validate(true)) return;

    setButtonDisabled(submitBtn, true);
    statusEl.textContent = 'Generando tu presupuesto…';

    let pdfBlob = null;
    let budget = null;

    try {
      const r = state.room || {};
      const totalPanels = Array.isArray(state.panels) ? state.panels.length : 0;
      const flat = state.results || {};
      const allOptions = (await buildAllOptions(state.room, state.surfaces)) || [];

      const reservedId = await reserveBudgetId({
        room: state.room,
        surfaces: state.surfaces,
        product: state.product
          ? {
              id: state.product.id,
              nombre: state.product.nombre,
              formato: state.product.formato,
              dimension: state.product.dimension,
              precio: state.product.precio,
            }
          : null,
        client: state.client,
      });

      budget = {
        id: reservedId || 'P-' + Date.now().toString(36).toUpperCase(),
        date: new Date(),
        client: state.client,
        room: {
          largo: r.largo,
          ancho: r.ancho,
          altura: r.altura,
          volume: (r.largo || 0) * (r.ancho || 0) * (r.altura || 0),
          totalSurface:
            2 * (r.largo || 0) * (r.ancho || 0) +
            2 * (r.largo || 0) * (r.altura || 0) +
            2 * (r.ancho || 0) * (r.altura || 0),
        },
        surfaces: state.surfaces,
        product: state.product,
        selectedOptionId: state.product?.id || null,
        options: allOptions,
        panels: { ceiling: totalPanels, walls: [0, 0, 0, 0], total: totalPanels },
        results: {
          bands: flat.bands || [],
          trBeforeMean: flat.trBeforeMean || 0,
          trAfterMean: flat.trAfterMean || 0,
          trAt500: { before: flat.trBeforeAt500 || 0, after: flat.trAfterAt500 || 0 },
          trAt1000: { before: flat.trBeforeAt1000 || 0, after: flat.trAfterAt1000 || 0 },
        },
        totalPriceEUR: (state.product?.precio || 0) * totalPanels,
      };

      // 1) Generar PDF.
      try {
        const pdfMod = await loadPdf();
        if (pdfMod?.generatePDF) {
          pdfBlob = await pdfMod.generatePDF(budget);
          update({ pdfBlob });
        }
      } catch (err) {
        console.error('[step5] generatePDF falló:', err);
      }

      // 2) SIEMPRE descargar el PDF al disco — el cliente lo tiene a mano,
      //    venga o no venga el email. Si el envío automático luego funciona,
      //    también recibirá el adjunto en su buzón.
      if (pdfBlob) {
        triggerLocalDownload(pdfBlob, `presupuesto-isinac-${budget.id}.pdf`);
      }

      // 3) Intentar envío automático por email vía Cloud Function.
      let emailSent = false;
      let backendError = null;
      try {
        const emailMod = await loadEmail();
        if (emailMod?.sendBudget && pdfBlob) {
          statusEl.textContent = 'Enviando por email…';
          const result = await emailMod.sendBudget(budget, pdfBlob);
          emailSent = result?.ok === true;
          if (!emailSent) {
            backendError = result?.error || 'desconocido';
            console.warn('[step5] backend rechazó:', backendError);
          }
        }
      } catch (err) {
        backendError = err?.message || 'red';
        console.warn('[step5] sendBudget falló:', err);
      }

      update({ emailSent, backendError });
      next();
    } catch (err) {
      console.error('[step5] envío falló:', err, { budget });
      try {
        if (pdfBlob && budget) {
          triggerLocalDownload(pdfBlob, `presupuesto-isinac-${budget.id || 'sin-id'}.pdf`);
        }
      } catch (_) {
        /* ignore */
      }
      const reason = err?.message ? ` (${String(err.message).slice(0, 80)})` : '';
      statusEl.textContent = `Algo no ha ido bien${reason}. Te hemos descargado el PDF.`;
      setButtonDisabled(submitBtn, false);
    }
  }

  function triggerLocalDownload(blob, filename) {
    try {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (err) {
      console.warn('[step5] no se pudo descargar el PDF:', err);
    }
  }

  function openMailto(budget) {
    const subject = encodeURIComponent(
      `Presupuesto ISINAC — ${budget.client?.name || ''}`
    );
    const body = encodeURIComponent(
      `Hola,\n\nAdjunto el presupuesto generado en el visualizador ISINAC.\n\n` +
        `Cliente: ${budget.client?.name || ''}\n` +
        `Email: ${budget.client?.email || ''}\n` +
        `Ciudad: ${budget.client?.city || ''}\n` +
        `Tipo de sala: ${budget.client?.roomType || ''}\n\n` +
        `Sala: ${budget.room?.largo} × ${budget.room?.ancho} × ${budget.room?.altura} m\n` +
        `Producto: ${budget.product?.nombre || ''}\n` +
        `Paneles: ${budget.panels?.total || 0}\n` +
        `Total con IVA: ${(budget.totalPriceEUR || 0).toLocaleString('es-ES', { maximumFractionDigits: 2 })} €\n\n` +
        `(Adjunta el PDF que se acaba de descargar a este correo antes de enviarlo).`
    );
    const to = budget.client?.email || 'info@isinac.com';
    const cc = 'info@isinac.com';
    const href = `mailto:${to}?cc=${cc}&subject=${subject}&body=${body}`;
    setTimeout(() => {
      window.location.href = href;
    }, 800);
  }

  // Validación inicial
  setButtonDisabled(submitBtn, !validate(false));
}
